
import Vue from "vue";
import { SidebarLinks } from "@/interfaces/candidate/nav_side_bar";
// import ChangeLanguage from "@/components/shared/ChangeLanguage.vue";
import { mapActions, mapGetters } from "vuex";
import {
  AUTH_LOADING,
  GET_COMPANY_DETAILS,
  GET_LATEST_WORK_HISTORY,
  GET_USER_DETAILS,
  LOGOUT_USER
} from "@/store/modules/auth/constants";
import { clean_string, get_name_initials } from "@/utils/global";
import {
  GET_ROLE_BY_NAME,
  INIT_NOTIFICATIONS,
  NOTIFICATION_ACTION,
  NOTIFICATION_ACTION_LOADING,
  NOTIFICATIONS,
  NOTIFICATIONS_COUNT,
  APP_ASSESSMENTS,
  SITE_DIRECTION,
  APP_ASSESSMENT_RESULT
} from "@/store/modules/common/constants";
import DataNotFound from "@/components/shared/DataNotFound.vue";
import {
  NotificationEventDetails,
  NotificationsResponse
} from "@/interfaces/responses/notifications";
import {
  UpdateNotificationStatusPayload,
  SiteDirections
} from "@/store/modules/common/interfaces";
import { request_assessment_resume } from "@/store/apis/recruiter";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import i18n from "@/i18n";
import { AppAssessments } from "@/interfaces/data_objects/app_assessments";
import { resolveEnglishAssessmentTypeByPersona } from "@/utils/assessment_persona";
// import AppPoweredBy from "@/components/shared/AppPoweredBy.vue";

export default Vue.extend({
  name: "NavSideBar",
  components: { DataNotFound },
  filters: {
    capitalize(value: string) {
      if (!value) return "";
      const str = String(value);
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
  },
  data() {
    return {
      notification_overlay: false,
      module: "",
      drawer: true,
      inception_logo: require("@/assets/logos/inception.svg"),
      dropdown_icon: require("@/assets/icons/linear/direction-down.svg"),
      notification_icon: require("@/assets/icons/linear/notification.svg"),
      active_route_icon: require("@/assets/icons/linear/speed-test.svg"),
      sidebar_links: [] as SidebarLinks[],
      logout_confirm_visible: false,
      logout_assessment_id: null as number | null
    };
  },
  watch: {
    get_site_direction() {
      this.set_sidebar();
    },
    companyDetail: {
      handler() {
        this.set_sidebar();
      },
      deep: true
    },
    $route() {
      if (this.notification_overlay) this.notification_overlay = false;
    },
    "$vuetify.breakpoint.mdAndDown"(isSmallScreen) {
      if (isSmallScreen) {
        this.drawer = false;
      } else {
        this.drawer = true;
      }
    }
  },
  created() {
    // Ensure stale logout flags don't suppress leave-confirmation dialogs
    (window as unknown as { __hcmsLoggingOut?: boolean }).__hcmsLoggingOut =
      false;
    if (this.$vuetify.breakpoint.mdAndDown) {
      this.drawer = false;
    }
  },
  computed: {
    SiteDirections() {
      return SiteDirections;
    },
    // Fully dynamic tenant logo (from DB branding). No static fallback:
    // branding store (pre-login slug) or the logged-in company's branding.
    hcms_logo(): string {
      return (
        this.$store.state.branding?.logo_uri ||
        this.companyDetail?.storage_path?.branding?.logo_uri ||
        ""
      );
    },
    // Assignment-driven tenants (no company assessment_setting) get a minimal
    // candidate sidebar (Dashboard only); the dashboard lists assigned assessments.
    is_assignment_driven(): boolean {
      return !this.companyDetail?.storage_path?.assessment_setting
        ?.candidates_english;
    },
    ...mapGetters("auth", {
      user: GET_USER_DETAILS,
      companyDetail: GET_COMPANY_DETAILS,
      get_latest_work_history: GET_LATEST_WORK_HISTORY,
      auth_loading: AUTH_LOADING
    }),
    ...mapGetters("common", {
      get_role_by_name: GET_ROLE_BY_NAME,
      get_notifications_count: NOTIFICATIONS_COUNT,
      get_notifications: NOTIFICATIONS,
      notification_action_loading: NOTIFICATION_ACTION_LOADING,
      app_assessments: APP_ASSESSMENTS,
      get_site_direction: SITE_DIRECTION
    }),
    if_assessment() {
      const route_name = clean_string(this.$route.name ?? "");
      return (
        route_name === "candidate-assessment" ||
        route_name === "inception-anotation-assessment" ||
        route_name === "inception-english-assessment" ||
        route_name === "english-assessment" ||
        route_name === "assessment-report" ||
        route_name === "assessment-completed" ||
        route_name === "annotation-assessment"
      );
    },
    /**
     * English assessment reports URL using the assessment version selected in Settings (Candidates > English).
     * e.g. english_v5 -> /recruiter/assessments/english/v5
     */
    english_assessment_reports_link(): string {
      const eng =
        this.companyDetail?.storage_path?.assessment_setting
          ?.candidates_english;
      const version = eng ? eng.replace("english_", "") : "v4";
      return `/recruiter/assessments/english/${version}`;
    }
  },
  async mounted() {
    this.set_sidebar();
    this.module = this.resolve_module();
    await this.init_notifications();
  },
  methods: {
    get_name_initials,
    capitalize(value: string) {
      if (!value) return "";
      const str = String(value);
      return str.charAt(0).toUpperCase() + str.slice(1);
    },
    ...mapActions("auth", {
      logout_user: LOGOUT_USER
    }),
    async _logout_user() {
      const routeName = String(this.$route?.name ?? "");
      const isCandidateAiAssessment =
        routeName === "candidate-ai-assessment" && !!this.$route?.params?.id;
      const isEnglishAssessment =
        routeName === "inception-english-assessment" ||
        routeName === "english-assessment";

      if (isCandidateAiAssessment) {
        // Store the assessment id and show custom dialog instead of native confirm
        this.logout_assessment_id = Number(this.$route?.params?.id) || null;
        this.logout_confirm_visible = true;
        return;
      }

      if (isEnglishAssessment) {
        this.logout_assessment_id = this.resolveEnglishAssessmentId();
        this.logout_confirm_visible = true;
        return;
      }

      // Non-assessment routes: logout immediately
      (window as unknown as { __hcmsLoggingOut?: boolean }).__hcmsLoggingOut =
        true;
      await this.logout_user();
      await this.$router.push("/auth/login");
    },
    async handleSidebarClick(link: SidebarLinks) {
      // For most links, fall back to normal navigation
      if (link.link !== "/candidate/assessments/english") {
        if (link.link) {
          await this.$router.push(link.link);
        }
        return;
      }

      // For English Assessment, check the latest assessment status first so we
      // can decide whether to enter the route at all.
      const assessmentId = this.resolveEnglishAssessmentId();

      if (!assessmentId) {
        await this.$router.push(link.link);
        return;
      }

      const assessment_exist = await this.$store.dispatch(
        `common/${APP_ASSESSMENT_RESULT}`,
        {
          assessment_id: assessmentId
        }
      );

      if (!assessment_exist) {
        await this.$router.push(link.link);
        return;
      }

      type AssessmentResultMeta = {
        status?: AppAssessments.AppAssessmentStatus | string | null;
        result?: {
          status?: AppAssessments.AppAssessmentStatus | string | null;
          overall_score?: number | null;
        } | null;
        resume_admin_status?: "PENDING" | "APPROVED" | "REJECTED" | null;
        overall_score?: number | null;
      };

      const typedResult = assessment_exist as AssessmentResultMeta;
      let rawStatus = typedResult.status || typedResult.result?.status || null;
      const adminStatus = typedResult.resume_admin_status ?? null;

      // Align with dashboard logic: if user just submitted and API still says IN_PROGRESS,
      // treat it as RESULT_PROCESSING so we show "Submitted" and block re-entry.
      let forceSubmitted = false;
      const JUST_SUBMITTED_TTL_MS = 2 * 60 * 1000; // 2 minutes
      const now = typeof Date.now === "function" ? Date.now() : 0;
      if (typeof sessionStorage !== "undefined") {
        const englishFlag = sessionStorage.getItem(
          "english_assessment_just_submitted"
        );
        const englishTs = sessionStorage.getItem(
          "english_assessment_just_submitted_ts"
        );
        const englishJustSubmitted =
          englishFlag === "1" ||
          (englishTs && now - parseInt(englishTs, 10) < JUST_SUBMITTED_TTL_MS);
        if (
          englishJustSubmitted &&
          rawStatus === AppAssessments.AppAssessmentStatus.IN_PROGRESS
        ) {
          sessionStorage.removeItem("english_assessment_just_submitted");
          sessionStorage.removeItem("english_assessment_just_submitted_ts");
          rawStatus = AppAssessments.AppAssessmentStatus.RESULT_PROCESSING;
          forceSubmitted = true;
        }
      }

      const isSubmitted =
        rawStatus === AppAssessments.AppAssessmentStatus.RESULT_PROCESSING ||
        rawStatus === AppAssessments.AppAssessmentStatus.COMPLETED;
      const isInProgress =
        rawStatus === AppAssessments.AppAssessmentStatus.IN_PROGRESS;
      const waitForApproval = isInProgress && adminStatus !== "APPROVED";

      if (forceSubmitted || isSubmitted) {
        this.$store.commit(
          ROOT_NOTIFICATION,
          i18n.t("assessments.completed-already")
        );
        if (this.$route.name !== "candidate-dashboard") {
          await this.$router.push("/candidate/dashboard");
        }
        return;
      }

      if (waitForApproval) {
        try {
          await request_assessment_resume(assessmentId);
        } catch (_e) {
          // ignore – still block direct resume
        }
        this.$store.commit(
          ROOT_NOTIFICATION,
          i18n.t("assessments.resume-awaiting-approval")
        );
        if (this.$route.name !== "candidate-dashboard") {
          await this.$router.push("/candidate/dashboard");
        }
        return;
      }

      // If we reach here we either have no result, a redo, or an approved
      // in-progress attempt that should allow entry.
      await this.$router.push(link.link);
    },
    resolveEnglishAssessmentId(): number | null {
      type MinimalAssessment = {
        id: number;
        assessment_type?: string | number | null;
      };
      const assessment_setting = resolveEnglishAssessmentTypeByPersona(
        this.user?.persona,
        this.companyDetail?.storage_path?.assessment_setting?.candidates_english
      );
      const list = Array.isArray(this.app_assessments)
        ? (this.app_assessments as MinimalAssessment[])
        : [];
      if (!assessment_setting || list.length === 0) return null;
      const assessment_setting_id = Number(assessment_setting);
      const has_numeric_assessment_setting = Number.isFinite(
        assessment_setting_id
      );

      const found = list.find(
        (val) =>
          val.assessment_type === assessment_setting ||
          (has_numeric_assessment_setting && val.id === assessment_setting_id)
      );
      return found?.id ?? null;
    },
    async confirmLogout() {
      const assessmentId = this.logout_assessment_id;
      this.logout_confirm_visible = false;

      if (assessmentId) {
        try {
          await request_assessment_resume(assessmentId);
        } catch (_e) {
          // non-blocking — backend may return 404 if no in-progress result exists
        }
      }

      // Mark that we're logging out so route guards can skip extra leave modals
      (window as unknown as { __hcmsLoggingOut?: boolean }).__hcmsLoggingOut =
        true;
      await this.logout_user();
      await this.$router.push("/auth/login");
      this.logout_assessment_id = null;
    },
    cancelLogout() {
      this.logout_confirm_visible = false;
      this.logout_assessment_id = null;
      (window as unknown as { __hcmsLoggingOut?: boolean }).__hcmsLoggingOut =
        false;
    },
    ...mapActions("common", {
      init_notifications: INIT_NOTIFICATIONS,
      notification_action: NOTIFICATION_ACTION
    }),
    async mark_all_as_read() {
      const ids = this.get_notifications.map(
        (notification: NotificationsResponse) => notification.id
      );
      const payload: UpdateNotificationStatusPayload = {
        notificationIds: ids
      };
      await this.notification_action(payload);
    },
    async dismiss_notification(notification: NotificationsResponse) {
      const payload: UpdateNotificationStatusPayload = {
        notificationIds: [notification.id]
      };
      await this.notification_action(payload);
    },
    async notification_navigate(notification: NotificationsResponse) {
      const event_details = NotificationEventDetails[notification.event_name];
      let route: string = event_details.route;
      if (event_details.navigation) route += `${notification.event_id}`;
      await this.$router.push(route);
      await this.dismiss_notification(notification);
      this.toggle_notification_overlay();
    },
    display_route_name(): string {
      const route_name = clean_string(this.$route.name ?? "");
      const module = clean_string(this.module);
      if (module === "candidate") {
        if (route_name === "candidate-dashboard") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/dashboard.svg");
          return this.$t("nav-sidebar.dashboard").toString();
        } else if (route_name === "dashboard-intro") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/dashboard.svg");
          return this.$t("nav-sidebar.dashboard-intro").toString();
        } else if (route_name === "survey-questions") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.survey").toString();
        } else if (route_name === "candidate-jobs") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/jobs.svg");
          return this.$t("nav-sidebar.matching-job").toString();
        } else if (route_name === "candidate-courses") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/courses.svg");
          return this.$t("nav-sidebar.courses").toString();
        } else if (
          route_name === "view-profile" ||
          route_name === "personal-profile"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/candidates.svg");
          return this.$t("nav-sidebar.profile").toString();
        } else if (
          route_name === "candidate-matching-job" ||
          route_name === "candidate-upskill-career"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/jobs.svg");
          return this.$t("nav-sidebar.jobs").toString();
        } else if (route_name === "accounts-setting") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
          return this.$t("nav-sidebar.settings").toString();
        } else if (route_name === "candidate-error") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
          return this.$t("nav-sidebar.error").toString();
        } else if (
          route_name === "candidates-interviews" ||
          route_name === "interview-start"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.interviews").toString();
        } else if (route_name === "profile-fetching") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.fetching").toString();
        } else if (route_name === "candidate-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment").toString();
        } else if (route_name === "inception-anotation-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.annotation-assessment").toString();
        } else if (route_name === "inception-english-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.english-assessment").toString();
        } else if (route_name === "english-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.english-assessment").toString();
        } else if (route_name === "assessment-report") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment-report").toString();
        } else if (route_name === "assessment-completed") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment-completed").toString();
        } else if (route_name === "annotation-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.annotation-assessment").toString();
        } else if (route_name === "candidate-ai-assessments") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.ai-assessments").toString();
        } else if (route_name === "candidate-ai-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.ai-assessment").toString();
        } else {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
          return this.$t("nav-sidebar.not-found").toString();
        }
      } else if (module === "recruiter" || module === "recruiter_admin") {
        if (route_name === "recruiter-dashboard") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/dashboard.svg");
          return this.$t("nav-sidebar.dashboard").toString();
        } else if (route_name === "survey-questions") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.survey").toString();
        } else if (route_name === "add-survey-question") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.survey").toString();
        } else if (route_name === "edit-survey-question") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.survey").toString();
        }
        // else if (
        //   route_name === "recruiter-jobs" ||
        //   route_name === "recruiter-job-listing" ||
        //   route_name === "recruiter-post-job" ||
        //   route_name === "recruiter-search-standardised-profile" ||
        //   route_name === "view-job-details" ||
        //   route_name === "recruiter-view-job" ||
        //   route_name === "recruiter-edit-job" ||
        //   route_name === "view-standardised-job" ||
        //   route_name === "edit-standardised-job"
        // ) {
        //   this.active_route_icon = require("@/assets/icons/nav_sidebar/active/jobs.svg");
        //   return this.$t("nav-sidebar.jobs").toString();
        // }
        else if (
          route_name === "recruiter-candidates" ||
          route_name === "recruiter-view-candidates"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/candidates.svg");
          return this.$t("nav-sidebar.candidates").toString();
        } else if (route_name === "candidate-profile") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/candidates.svg");
          return this.$t("nav-sidebar.matching-candidates").toString();
        } else if (route_name === "all-assessments") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/candidates.svg");
          return this.$t("nav-sidebar.assessment").toString();
        } else if (
          route_name === "recruiter-employees" ||
          route_name === "recruiter-view-employees"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/employees.svg");
          return this.$t("nav-sidebar.employees").toString();
        }
        // else if (route_name === "skills-analytics") {
        //   return "Skills Analytics";
        // }
        else if (
          route_name === "accounts-setting" ||
          route_name === "assessments-setting" ||
          route_name === "approval-criteria"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
          return this.$t("nav-sidebar.settings").toString();
        } else if (
          route_name === "edit-email-templates" ||
          route_name === "email-templates"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
          return this.$t("nav-sidebar.settings").toString();
        } else if (route_name === "view-profile") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/candidates.svg");
          return this.$t("nav-sidebar.profile").toString();
        } else if (route_name === "recruiter-interviews") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.interviews").toString();
        } else if (
          route_name === "assessment-listing" ||
          route_name === "assessment-report-recruiter"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment").toString();
        } else if (route_name === "english-assessment-reports") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment-reports").toString();
        } else if (route_name === "annotation-assessment-reports") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment-reports").toString();
        } else if (route_name === "ai-assessments-home") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.ai-assessments").toString();
        } else if (route_name === "ai-assessment-reports") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment-reports").toString();
        } else if (route_name === "ai-assessment-report") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.assessment-report").toString();
        } else if (route_name === "create-ai-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.ai-assessment-creation").toString();
        } else if (route_name === "english-assessment-report") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.english-assessment-report").toString();
        } else if (route_name === "annotation-assessment-report") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.annotation-assessment-report").toString();
        } else if (route_name === "overall-assessment-report") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return "Overall Result";
        } else if (route_name === "edit-ai-assessment") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.edit-assessment").toString();
        } else if (
          route_name === "assessment-report" // todo [later discuss with faiz bhai]
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/teacher.svg");
          return this.$t("nav-sidebar.report-details").toString();
        } else if (
          route_name === "admin" ||
          route_name === "all-candidates" ||
          route_name === "view-candidate"
        ) {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/approvals.svg");
          return this.$t("nav-sidebar.approvals").toString();
        } else if (route_name === "cancelled-assessments") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/approvals.svg");
          return this.$t("nav-sidebar.cancelled-assessments").toString();
        } else if (route_name === "redo-assessments") {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/approvals.svg");
          return this.$t("nav-sidebar.redo-assessments").toString();
        } else {
          this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
          return this.$t("nav-sidebar.not-found").toString();
        }
      } else {
        this.active_route_icon = require("@/assets/icons/nav_sidebar/active/settings.svg");
        return this.$t("nav-sidebar.not-found").toString();
      }
    },
    getTitle(): string {
      return this.$t("shared.not-found").toString();
    },
    /**
     * Resolve the sidebar module for the current user.
     * Prefer the user's own role_name (tenant-agnostic — set on SSO/rehydrate).
     * Fall back to id-matching against the (domain) roles list for flows where
     * role_name isn't present yet (e.g. fresh in-app login), guarded so a missing
     * role never throws.
     */
    resolve_module(): string {
      const role_name = String(this.user?.role_name || "").toLowerCase();
      if (role_name === "recruiter") return "recruiter";
      if (role_name === "recruiter admin") return "recruiter_admin";
      if (role_name === "individual") return "candidate";
      // Fallback: match role ids within the same (domain) company.
      const recruiter = this.get_role_by_name("recruiter");
      const recruiter_admin = this.get_role_by_name("recruiter admin");
      if (recruiter && recruiter.id === this.user?.role_id) return "recruiter";
      if (recruiter_admin && recruiter_admin.id === this.user?.role_id)
        return "recruiter_admin";
      return "candidate";
    },
    /**
     * Function to set sidebar links array
     */
    set_sidebar() {
      this.sidebar_links = [
        {
          name: this.$t("nav-sidebar.dashboard").toString(),
          link: "/candidate/dashboard",
          icon: require("@/assets/icons/nav_sidebar/dashboard.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/dashboard.svg"),
          module: ["candidate"],
          children: []
        },
        // {
        //   name: this.$t("nav-sidebar.survey").toString(),
        //   link: "/candidate/survey",
        //   icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
        //   module: ["candidate"],
        //   children: []
        // },
        // {
        //   name: this.$t("nav-sidebar.dashboard-intro").toString(),
        //   link: "/candidate/dashboard-intro",
        //   icon: require("@/assets/icons/nav_sidebar/dashboard.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/dashboard.svg"),
        //   module: ["candidate"],
        //   children: []
        // },
        // {
        //   name: this.$t("nav-sidebar.jobs").toString(),
        //   link: "/candidate/jobs",
        //   icon: require("@/assets/icons/nav_sidebar/jobs.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/jobs.svg"),
        //   module: ["candidate"],
        //   children: []
        // },
        // {
        //   name: this.$t("nav-sidebar.interviews").toString(),
        //   link: "/candidate/interviews",
        //   icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
        //   module: ["candidate"],
        //   children: []
        // },
        // {
        //   name: this.$t("nav-sidebar.assessment").toString(),
        //   link: "/candidate/assessments",
        //   icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
        //   module: ["candidate"],
        //   children: []
        // },
        // Candidate extras — only for config-driven tenants (e.g. GT). Assignment-
        // driven tenants (e.g. people-grid) see just the Dashboard.
        ...(this.is_assignment_driven
          ? []
          : [
              {
                name: this.$t("nav-sidebar.english-assessment").toString(),
                link: "/candidate/assessments/english",
                icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
                active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
                module: ["candidate"],
                children: []
              },
              {
                name: this.$t("nav-sidebar.ai-assessments").toString(),
                link: "/candidate/ai-assessments",
                icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
                active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
                module: ["candidate"],
                children: []
              },
              {
                name: this.$t("nav-sidebar.settings").toString(),
                link: "/settings/account",
                icon: require("@/assets/icons/nav_sidebar/settings.svg"),
                active_icon: require("@/assets/icons/nav_sidebar/active/settings.svg"),
                module: ["candidate"],
                children: [
                  {
                    name: this.$t("nav-sidebar.account").toString(),
                    link: "/settings/account",
                    icon: require("@/assets/icons/nav_sidebar/settings.svg"),
                    active_icon: require("@/assets/icons/nav_sidebar/active/settings.svg"),
                    module: ["candidate"],
                    children: []
                  }
                ]
              }
            ]),
        {
          name: this.$t("nav-sidebar.dashboard").toString(),
          link: "/recruiter/dashboard",
          icon: require("@/assets/icons/nav_sidebar/dashboard.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/dashboard.svg"),
          module: ["recruiter", "recruiter_admin"],
          children: []
        },

        // {
        //   name: this.$t("nav-sidebar.jobs").toString(),
        //   icon: require("@/assets/icons/nav_sidebar/jobs.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/jobs.svg"),
        //   link: "/recruiter/jobs",
        //   module: ["recruiter", "recruiter_admin"],
        //   children: [
        //     {
        //       name: this.$t("nav-sidebar.job-listing").toString(),
        //       link: "/recruiter/jobs",
        //       module: ["recruiter", "recruiter_admin"],
        //       children: []
        //     },
        //     {
        //       name: this.$t("nav-sidebar.post-job").toString(),
        //       module: ["recruiter", "recruiter_admin"],
        //       link: "/recruiter/jobs/post-job",
        //       children: []
        //     }
        //   ]
        // },
        // {
        //   name: this.$t("nav-sidebar.interviews").toString(),
        //   link: "/recruiter/interviews",
        //   icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
        //   module: ["recruiter", "recruiter_admin"],
        //   children: []
        // },
        // {
        //   name: this.$t("nav-sidebar.assessment").toString(),
        //   link: "/recruiter/assessments",
        //   icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
        //   active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
        //   module: ["recruiter", "recruiter_admin"],
        //   children: []
        // },
        {
          name: this.$t("nav-sidebar.assessment-reports").toString(),
          link: this.english_assessment_reports_link,
          icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
          module: ["recruiter", "recruiter_admin"],
          children: []
        },
        {
          name: "Additional Assessment Reports",
          link: "/recruiter/assessments/ai",
          icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
          module: ["recruiter", "recruiter_admin"],
          children: []
        },
        {
          name: this.$t("nav-sidebar.summary-report").toString(),
          link: "/recruiter/overall-result/summary-report",
          icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
          module: ["recruiter", "recruiter_admin"],
          children: []
        },
        {
          name: this.$t("nav-sidebar.ai-assessments").toString(),
          module: ["recruiter", "recruiter_admin"],
          link: "/recruiter/ai-assessments",
          icon: require("@/assets/icons/nav_sidebar/teacher.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/teacher.svg"),
          children: []
        },
        {
          name: this.$t("nav-sidebar.candidates").toString(),
          link: "/recruiter/candidates",
          icon: require("@/assets/icons/nav_sidebar/candidates.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/candidates.svg"),
          module: ["recruiter", "recruiter_admin"],
          children: []
        },
        {
          name: this.$t("nav-sidebar.assessment-result").toString(),
          link: "/recruiter/overall-result",
          icon: require("@/assets/icons/nav_sidebar/approvals.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/approvals.svg"),
          module: ["recruiter_admin"],
          children: []
        },
        {
          name: this.$t("nav-sidebar.employees").toString(),
          link: "/recruiter/employees",
          icon: require("@/assets/icons/nav_sidebar/employees.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/employees.svg"),
          module: ["recruiter"],
          children: []
        },
        {
          name: this.$t("nav-sidebar.settings").toString(),
          link: "/settings",
          icon: require("@/assets/icons/nav_sidebar/settings.svg"),
          active_icon: require("@/assets/icons/nav_sidebar/active/settings.svg"),
          module: ["recruiter", "recruiter_admin"],
          children: [
            {
              name: this.$t("nav-sidebar.account").toString(),
              link: "/settings/account",
              module: ["recruiter", "recruiter_admin"],
              children: []
            },
            {
              name: this.$t("nav-sidebar.assessments").toString(),
              link: "/settings/assessments",
              active_icon: require("@/assets/icons/nav_sidebar/active/settings.svg"),
              module: ["recruiter", "recruiter_admin"],
              children: []
            },
            {
              name: this.$t("nav-sidebar.approval-criteria").toString(),
              link: "/settings/approval-criteria",
              active_icon: require("@/assets/icons/nav_sidebar/active/settings.svg"),
              module: ["recruiter", "recruiter_admin"],
              children: []
            },
            {
              name: this.$t("nav-sidebar.email-templates").toString(),
              link: "/settings/email-templates",
              active_icon: require("@/assets/icons/nav_sidebar/active/settings.svg"),
              module: ["recruiter", "recruiter_admin"],
              children: []
            }
          ]
        }
      ];
    },
    toggle_notification_overlay() {
      this.notification_overlay = !this.notification_overlay;
    },
    handleOutsideClick() {
      if (this.notification_overlay) this.notification_overlay = false;
    }
  }
});
